import api from '~/libs/api';
import {
  TGeneralInfoAssetManager,
  TGeneralInfoLegalResident,
  TGeneralInfoSoleTrader,
} from './types';
import { infoMessagesAdditionQuestionnaire } from '~/modules/identification/forms/GeneralInfo/constants.ts';
import { ClientTypes } from '~/constants/clientTypes.ts';
import { getAddressInitialValues, getDocumentsInitialValues } from '../../utils';
import { ukraineOption } from '../../constants';

export const getSoleTraderInitialValues = (formData: TGeneralInfoSoleTrader) => ({
  fullName: formData.fullName || '',
  address: {
    ...getAddressInitialValues(formData),
    birthplaceCountry: formData?.address?.birthplaceCountry || ukraineOption,
    citizenshipCountry: formData?.address?.citizenshipCountry || ukraineOption,
    residenceCountry: formData?.address?.residenceCountry || ukraineOption,
  },
  birthDate: formData.birthDate,
  birthPlace: formData.birthPlace || '',
  uniqueDemographicRecordNumber: formData.uniqueDemographicRecordNumber || '',
  documents: getDocumentsInitialValues(formData),
  financialPhone: formData?.financialPhone || '',
  contactNumbers: formData?.contactNumbers?.length ? formData.contactNumbers : [''],
  emailAddress:
    formData?.emailAddress && formData.emailAddress.length > 0 && formData.emailAddress[0] !== null
      ? formData.emailAddress
      : [''],
  license: formData?.license,
  licenses:
    formData?.licenses?.length > 0
      ? formData.licenses.map((lic) => ({
          licenseName: lic?.licenseName || '',
          licenseDate: lic?.licenseDate || null,
          licenseNumber: lic?.licenseNumber || '',
          licenseOrganisationCreate: lic?.licenseOrganisationCreate || '',
          licenseExpiryTerm: lic?.licenseExpiryTerm || null,
        }))
      : [],
  questionnaire: {
    question1: formData?.questionnaire?.question1,
    question2: formData?.questionnaire?.question2,
    question3: formData?.questionnaire?.question3,
    question4: formData?.questionnaire?.question4,
    question5: formData?.questionnaire?.question5,
    question6: formData?.questionnaire?.question6,
    question7: formData?.questionnaire?.question7,
    question8: formData?.questionnaire?.question8,
    question9: formData?.questionnaire?.question9,
  },
  isUSTaxResident: formData?.isUSTaxResident,
  isOtherTaxResident: formData?.isOtherTaxResident,
  cerOfLossOfUSCitizenship: formData?.cerOfLossOfUSCitizenship,
  explainRegardingLackOfUSCitizenship: formData?.explainRegardingLackOfUSCitizenship,
  taxIdentNumber: formData?.taxIdentNumber,
  taxstatusinternation: formData?.taxstatusinternation,
  fullNameInEng: formData?.fullNameInEng,
  businessabroad: formData?.businessabroad,
  'CRS-I': formData?.['CRS-I'] ?? null,
  type: formData?.type || null,
  foreignLegalEntity: null,
});

export const getLegalResidentInitialValues = (
  formData: TGeneralInfoLegalResident,
  isAdditionalInformationAboutTaxResidencyEnabled: boolean
) => ({
  fullNameUkr: formData.fullNameUkr || '',
  fullNameEng: formData.fullNameEng || '',
  shortNameUkr: formData.shortNameUkr || '',
  employeeCount: formData.employeeCount || null,
  address: getAddressInitialValues(formData),
  financialPhone: formData?.financialPhone || '',
  contactNumbers: formData?.contactNumbers?.length ? formData.contactNumbers : [''],
  emailAddress:
    formData?.emailAddress && formData.emailAddress.length > 0 && formData.emailAddress[0] !== null
      ? formData.emailAddress
      : [''],
  questionnaire: {
    question1: formData?.questionnaire?.question1,
    question2: formData?.questionnaire?.question2,
    question3: formData?.questionnaire?.question3,
    question4: formData?.questionnaire?.question4,
    question5: formData?.questionnaire?.question5,
    question6: formData?.questionnaire?.question6,
    question7: formData?.questionnaire?.question7,
    question8: formData?.questionnaire?.question8,
    question9: formData?.questionnaire?.question9,
    question10: formData?.questionnaire?.question10,
    question11: formData?.questionnaire?.question11,
    question12: formData?.questionnaire?.question12,
    question13: formData?.questionnaire?.question13,
    question14: formData?.questionnaire?.question14,
  },
  isOtherTaxResident: isAdditionalInformationAboutTaxResidencyEnabled
    ? formData?.isOtherTaxResident
    : !!formData?.isOtherTaxResident,
  isControllingPersonTaxResidentOtherJurisdiction: isAdditionalInformationAboutTaxResidencyEnabled
    ? formData?.isControllingPersonTaxResidentOtherJurisdiction
    : !!formData?.isControllingPersonTaxResidentOtherJurisdiction,
  isUSTaxResident: !!formData?.isUSTaxResident,
  isForeignFinancialUnderFATCA: !!formData?.isForeignFinancialUnderFATCA,
  hasForeignShare: !!formData?.hasForeignShare,
  'CRS-E': null,
  type: formData?.type || null,
  foreignLegalEntity: null,
});

export const getAssetManagerInitialValues = (formData: TGeneralInfoAssetManager) => ({
  lastName: formData?.lastName || '',
  firstName: formData?.firstName || '',
  middleName: formData?.middleName || '',
  birthDate: formData?.birthDate || null,
  identificationNumber: formData?.identificationNumber || '',
  foreignTaxNumber: formData?.foreignTaxNumber || '',
  address: getAddressInitialValues(formData),
  birthplaceCountry: formData?.birthplaceCountry || ukraineOption,
  citizenshipCountry: formData?.citizenshipCountry || ukraineOption,
  residenceCountry: formData?.residenceCountry || ukraineOption,
  birthPlace: formData?.birthPlace || '',
  documents: getDocumentsInitialValues(formData),
  contactNumbers: formData?.contactNumbers?.length ? formData.contactNumbers : [''],
  emailAddress:
    formData?.emailAddress && formData.emailAddress.length > 0 && formData.emailAddress[0] !== null
      ? formData.emailAddress
      : [''],
  isDataProcessingAccepted: !!formData.isDataProcessingAccepted,
  verification: formData.verification || 0,
  isOtherTaxResident: !!formData.isOtherTaxResident,
  isUSTaxResident: !!formData.isUSTaxResident,
});

export const getInfoClassifierCommunities = async (cityId: number | string) => {
  try {
    const { data } = await api.get(`/dictionary-service/city/${cityId}/nat-classif`);
    return data;
  } catch {
    return null;
  }
};

export const getTypeMessages = (
  clientType: string
): { headerMessage: string; infoMessage: string } => {
  if (clientType === ClientTypes.SOLE_TRADER) {
    return {
      headerMessage:
        'Додаток 1 до Анкети-опитувальника відповідно до постанови №26 від 16 березня 2023р.',
      infoMessage: infoMessagesAdditionQuestionnaire.SOLE_TRADER,
    };
  } else if (clientType === ClientTypes.LEGAL_RESIDENT) {
    return {
      headerMessage:
        'Додаток 2 до Анкети-опитувальника відповідно до постанови №26 від 16 березня 2023р.',
      infoMessage: infoMessagesAdditionQuestionnaire.LEGAL_RESIDENT,
    };
  }
  return { headerMessage: '', infoMessage: '' };
};
